/**
 * An organizer plan waiting for the attorney, inline under its run line: what
 * moves where, and Apply / Reject. Applying here is the same service call as
 * the agent's `apply_autocategorize_plan`.
 */
import { Check, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  FilesystemAPI,
  type AutocategorizeRunRecord
} from "../../../caseFiles/api/filesystemApi";
import { nodeTitle } from "../../../caseFiles/nodeNames";
import type { FsNode } from "../../../caseFiles/types";
import { useCaseIdeOptional } from "../../context/CaseIdeContext";
import { useCaseDataOptional } from "../../data/CaseDataContext";

/** Rows shown before "Show All". */
const PREVIEW_ROWS = 8;

/** The top level of the tree, as a folder path names it. */
export const CASE_FILES_ROOT = "Case Files";

export type OrganizerChange = {
  nodeId: string;
  file: string;
  from: string;
  to: string;
  documentType: string | null;
};

/**
 * One row per moved file, the first `PREVIEW_ROWS` until Show All. With
 * `showFrom` the origin gets its own column; otherwise it is the hover title.
 */
export const OrganizerMovesTable: FC<{
  changes: OrganizerChange[];
  showFrom?: boolean;
}> = ({ changes, showFrom = false }) => {
  const [showAll, setShowAll] = useState(false);
  if (!changes.length) return null;
  const shown = showAll ? changes : changes.slice(0, PREVIEW_ROWS);
  return (
    <>
      <table className="w-full table-fixed text-xs">
        <thead className="text-left text-fg-3">
          <tr>
            <th
              className={cn("pb-1 font-medium", showFrom ? "w-2/5" : "w-1/2")}
            >
              File
            </th>
            {showFrom ? <th className="pb-1 font-medium">From</th> : null}
            <th className="pb-1 font-medium">{showFrom ? "To" : "Moves To"}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line-1">
          {shown.map((c) => (
            <tr key={c.nodeId} data-testid="organizer-change">
              <td className="truncate py-1 pr-2" title={c.file}>
                {c.file}
                {c.documentType ? (
                  <span className="block truncate text-fg-3">
                    {c.documentType}
                  </span>
                ) : null}
              </td>
              {showFrom ? (
                <td className="truncate py-1 pr-2 text-fg-3" title={c.from}>
                  {c.from}
                </td>
              ) : null}
              <td
                className="truncate py-1"
                title={showFrom ? c.to : `From ${c.from}`}
              >
                {c.to}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {changes.length > PREVIEW_ROWS && !showAll ? (
        <Button
          variant="ghost"
          size="xs"
          className="-ml-1.5 self-start text-fg-3"
          onClick={() => setShowAll(true)}
        >
          Show All {changes.length}
        </Button>
      ) : null}
    </>
  );
};

const folderPath = (node: FsNode | undefined, byId: Map<string, FsNode>) => {
  const names: string[] = [];
  let current = node;
  while (current) {
    names.unshift(nodeTitle(current));
    current = current.parent_id ? byId.get(current.parent_id) : undefined;
  }
  return names.join(" / ");
};

/** The moves a plan makes, one per file whose folder changes. */
export const organizerChanges = (
  plan: NonNullable<AutocategorizeRunRecord["plan"]>,
  nodes: FsNode[]
): OrganizerChange[] => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const planned = new Map(plan.folders.map((f) => [f.temp_id, f]));
  const plannedPath = (tempId: string): string => {
    const names: string[] = [];
    let folder = planned.get(tempId);
    while (folder) {
      names.unshift(folder.name);
      folder = folder.parent_temp_id
        ? planned.get(folder.parent_temp_id)
        : undefined;
    }
    return names.join(" / ");
  };
  return [...plan.placements]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .flatMap((p) => {
      const node = byId.get(p.node_id);
      const to = p.target_folder_temp_id
        ? plannedPath(p.target_folder_temp_id)
        : p.target_folder_id
        ? folderPath(byId.get(p.target_folder_id), byId)
        : "";
      const from = node?.parent_id
        ? folderPath(byId.get(node.parent_id), byId)
        : CASE_FILES_ROOT;
      if (!to || to === from) return [];
      return [
        {
          nodeId: p.node_id,
          file: node ? nodeTitle(node) : p.title ?? "A file",
          from,
          to,
          documentType: p.document_type ?? null
        }
      ];
    });
};

type Decision = "applied" | "rejected";

const Resolved: FC<{ decision: Decision; count: number }> = ({
  decision,
  count
}) => (
  <div
    className="text-xs text-muted-foreground"
    data-testid="organizer-plan-resolved"
  >
    {decision === "applied"
      ? `Applied: ${count} file${count === 1 ? "" : "s"} moved.`
      : "Rejected: nothing moved."}
  </div>
);

type Props = { individualId: string; runId: string };

export const OrganizerPlanCard: FC<Props> = ({ individualId, runId }) => {
  const ide = useCaseIdeOptional();
  const nodes = useCaseDataOptional()?.fs.nodes;
  const [record, setRecord] = useState<AutocategorizeRunRecord | null>(null);
  const [busy, setBusy] = useState<Decision | null>(null);
  const [decided, setDecided] = useState<Decision | null>(null);

  useEffect(() => {
    let cancelled = false;
    FilesystemAPI.getAutocategorizeRun(individualId, runId)
      .then(({ run }) => {
        if (!cancelled) setRecord(run);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [individualId, runId]);

  const changes = useMemo(
    () => (record?.plan ? organizerChanges(record.plan, nodes ?? []) : []),
    [record, nodes]
  );

  if (!record || record.status !== "proposed" || !record.plan) {
    return decided ? (
      <Resolved decision={decided} count={changes.length} />
    ) : null;
  }
  if (decided) return <Resolved decision={decided} count={changes.length} />;

  const decide = async (decision: Decision) => {
    setBusy(decision);
    try {
      const { run } =
        decision === "applied"
          ? await FilesystemAPI.applyAutocategorizeRun(individualId, runId)
          : await FilesystemAPI.cancelAutocategorizeRun(individualId, runId);
      setRecord(run);
      setDecided(decision);
      ide?.invalidate(["fs", "packet", "autocategorize"]);
      toast.success(
        decision === "applied"
          ? `Organized ${changes.length} file${changes.length === 1 ? "" : "s"}`
          : "Organizer plan rejected; nothing moved"
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't update the plan"
      );
    } finally {
      setBusy(null);
    }
  };

  const unplaced = record.plan.unplaced?.length ?? 0;
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border bg-background px-2.5 py-2 text-xs"
      data-testid="organizer-plan-card"
    >
      <div className="text-muted-foreground">
        {changes.length
          ? `${changes.length} file${
              changes.length === 1 ? "" : "s"
            } would move`
          : "Nothing would move"}
        {unplaced ? ` · ${unplaced} left where they are` : ""}
      </div>
      <OrganizerMovesTable changes={changes} />
      <div className="flex items-center gap-1.5">
        <Button
          variant="primary"
          size="xs"
          className="h-6 gap-1"
          disabled={busy !== null}
          onClick={() => void decide("applied")}
          data-testid="organizer-apply"
        >
          {busy === "applied" ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Check className="size-3" />
          )}
          Apply
        </Button>
        <Button
          size="xs"
          variant="ghost"
          className="h-6 gap-1"
          disabled={busy !== null}
          onClick={() => void decide("rejected")}
          data-testid="organizer-reject"
        >
          {busy === "rejected" ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <X className="size-3" />
          )}
          Reject
        </Button>
      </div>
    </div>
  );
};
